<template>
  <v-app class="custom__bg mt-1">
    <v-container>
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <div class="d-flex align-center justify-space-between mb-4 mt-2">
            <div>
              <h2 style="font-size: 24px; font-weight: 700; color: #333">Notifications</h2>
              <p class="grey--text text--darken-1 mb-0" style="font-size: 14px">Stay updated with your learning community</p>
            </div>
            <v-btn 
              text 
              color="#f69227" 
              class="text-capitalize hover-scale font-weight-bold" 
              @click="markAllRead"
            >
              <v-icon left small>mdi-check-all</v-icon>
              Mark all as read
            </v-btn>
          </div>

          <!-- Notification Filters -->
          <v-card class="premium-card mb-6 pa-2">
            <v-tabs v-model="tab" color="#f69227" show-arrows slider-color="#f69227">
              <v-tab class="text-capitalize font-weight-medium">All</v-tab>
              <v-tab class="text-capitalize font-weight-medium">
                <v-badge color="error" dot :value="true" offset-x="-5" offset-y="5">Mentions</v-badge>
              </v-tab>
              <v-tab class="text-capitalize font-weight-medium">Following</v-tab>
              <v-tab class="text-capitalize font-weight-medium">Groups</v-tab>
              <v-tab class="text-capitalize font-weight-medium">System</v-tab>
            </v-tabs>
          </v-card>

          <!-- Notification List -->
          <v-card class="premium-card overflow-hidden">
            <v-list class="pa-0">
              <template v-for="(item, index) in filteredNotifications">
                <v-list-item 
                  :key="item.id" 
                  class="pa-4 notification-item animate-fadeInUp"
                  :class="{'unread-bg': !item.read, 'delay-1': index < 6}"
                  style="transition: background-color 0.3s ease"
                  @click="markAsRead(item.id)"
                >
                  <v-list-item-avatar size="48" class="mr-4 my-0 align-self-start mt-1">
                    <!-- Icon Avatar for System notifications -->
                    <v-avatar v-if="item.type === 'system'" :color="getIconColor(item.icon)" size="48">
                      <v-icon color="white">{{ item.icon }}</v-icon>
                    </v-avatar>
                    <!-- User Avatar for interactions -->
                    <v-avatar v-else size="48" class="elevation-1">
                      <img :src="item.avatar" alt="avatar" />
                    </v-avatar>
                  </v-list-item-avatar>
                  
                  <v-list-item-content class="py-0">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <v-list-item-title class="font-weight-bold" style="font-size: 15px; color: #333; line-height: 1.4; white-space: normal">
                        <!-- Action-based bolding -->
                        <span v-if="item.type !== 'system'">
                          <span style="color: #2089d6">{{ item.user }}</span>
                          <span class="font-weight-regular"> {{ item.action }} </span>
                          <span v-if="item.target">"{{ item.target }}"</span>
                        </span>
                        <span v-else>{{ item.title }}</span>
                      </v-list-item-title>
                      <div class="d-flex align-center ml-4 flex-shrink-0">
                        <span class="grey--text caption">{{ item.timeAgo }}</span>
                        <!-- Unread Dot -->
                        <div v-if="!item.read" class="unread-indicator ml-2"></div>
                      </div>
                    </div>
                    
                    <v-list-item-subtitle v-if="item.message" class="mt-1" style="font-size: 13px; color: #555; white-space: normal; line-height: 1.5">
                      {{ item.message }}
                    </v-list-item-subtitle>
                    
                    <!-- Action Buttons -->
                    <div v-if="item.type === 'follow' && !item.read" class="mt-3">
                      <v-btn small color="#f69227" class="white--text text-capitalize mr-2 hover-scale" rounded elevation="1">Follow Back</v-btn>
                      <v-btn small outlined color="grey darken-1" class="text-capitalize hover-scale" rounded>Ignore</v-btn>
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-divider :key="'div-'+item.id" v-if="index < filteredNotifications.length - 1"></v-divider>
              </template>
              
              <!-- Empty State -->
              <div v-if="filteredNotifications.length === 0" class="pa-10 text-center">
                <v-icon size="64" color="grey lighten-2">mdi-bell-sleep</v-icon>
                <h3 class="mt-4 grey--text text--darken-1 font-weight-medium">All caught up!</h3>
                <p class="grey--text caption">You have no notifications in this category.</p>
              </div>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
export default {
  data: () => ({
    tab: 0,
    notifications: [
      {
        id: 1,
        type: 'mention',
        user: 'Prof. Ibrahim Musa',
        action: 'mentioned you in the discussion',
        target: 'How to solve simultaneous equations in under 2 minutes?',
        message: '@Shedrach Yohanna can you provide your approach to question 4?',
        avatar: 'https://randomuser.me/api/portraits/men/55.jpg',
        timeAgo: '10m ago',
        read: false,
        category: 1
      },
      {
        id: 2,
        type: 'follow',
        user: 'Fatima Usman',
        action: 'started following you',
        target: '',
        message: '',
        avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
        timeAgo: '1h ago',
        read: false,
        category: 2
      },
      {
        id: 3,
        type: 'group',
        user: 'Ahmadu Bello University Group',
        action: 'has a new announcement:',
        target: '',
        message: 'The midterm past questions have been uploaded to the group files section.',
        avatar: 'https://upload.wikimedia.org/wikipedia/en/thumb/5/52/Ahmadu_Bello_University_logo.png/220px-Ahmadu_Bello_University_logo.png',
        timeAgo: '3h ago',
        read: false,
        category: 3
      },
      {
        id: 4,
        type: 'system',
        title: 'New Quiz Available: Physics Motion & Forces',
        action: '',
        target: '',
        message: 'Test your knowledge on the latest quiz uploaded to the Quiz Bank.',
        icon: 'mdi-brain',
        timeAgo: 'Yesterday',
        read: true,
        category: 4
      },
      {
        id: 5,
        type: 'interaction',
        user: 'Yusuf Aliyu',
        action: 'applauded your lecture note',
        target: 'Thermodynamics Summary & Formulas',
        message: '',
        avatar: 'https://randomuser.me/api/portraits/men/22.jpg',
        timeAgo: 'Yesterday',
        read: true,
        category: 0
      },
      {
        id: 6,
        type: 'system',
        title: 'Welcome to janguAsk! 🎉',
        action: '',
        target: '',
        message: 'Complete your profile to connect with classmates and discover tailored educational content.',
        icon: 'mdi-party-popper',
        timeAgo: '2 days ago',
        read: true,
        category: 4
      },
      {
        id: 7,
        type: 'group',
        user: 'JAMB Mathematics Prep',
        action: 'Grace Okafor posted a new message in',
        target: 'JAMB Mathematics Prep',
        message: '"I have some comprehensive notes on it. I can share them here if you want."',
        avatar: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=100&h=100&fit=crop',
        timeAgo: '3 days ago',
        read: true,
        category: 3
      }
    ]
  }),
  computed: {
    filteredNotifications() {
      if (this.tab === 0) return this.notifications;
      return this.notifications.filter(n => n.category === this.tab || (this.tab === 4 && n.type === 'system'));
    }
  },
  methods: {
    getIconColor(icon) {
      const colors = {
        'mdi-brain': '#cd67f7',
        'mdi-party-popper': '#f69227',
        'mdi-information': '#2089d6',
        'mdi-alert': '#ef5350'
      };
      return colors[icon] || '#757575';
    },
    markAsRead(id) {
      const notif = this.notifications.find(n => n.id === id);
      if (notif && !notif.read) {
        notif.read = true;
      }
    },
    markAllRead() {
      this.notifications.forEach(n => n.read = true);
      this.$root.showSnackbar("All notifications marked as read", "#43a047", "mdi-check-all");
    }
  }
};
</script>

<style scoped>
.custom__bg {
  background: #f5f5f5;
  min-height: 100vh;
}
.notification-item:hover {
  background-color: #fafbfc;
  cursor: pointer;
}
.unread-bg {
  background-color: rgba(246, 146, 39, 0.05);
}
.unread-bg:hover {
  background-color: rgba(246, 146, 39, 0.08);
}
.unread-indicator {
  width: 10px;
  height: 10px;
  background-color: #f69227;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(246, 146, 39, 0.5);
}
</style>
